/**
 * AI-assisted draft reply UI: signal matrix, variant cycling, edit, and send from within a conversation.
 *
 * Where: embedded in `ConversationView` above/beside the composer.
 * Uses: `chatApi` draft endpoints; `conversationSignalsCatalog` for grouping and colors.
 * Behavior: matrix vs custom text modes; loading states around generation and send.
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  TextField,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  AutoAwesome as AiIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Edit as EditIcon,
  Close as DismissIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import type { Chat, MatchStrength, DraftVariant } from '@/types/chat';
import {
  buildConversationSignalUiMap,
  getFallbackSignalUi,
  getConversationSignalUi,
} from '@/constants/conversationSignalsCatalog';
import { chatApi } from '@/api/chat';

type CustomDraftMode = 'matrix' | 'own';

const MATCH_STRENGTH_COLORS: Record<MatchStrength, 'success' | 'info' | 'warning' | 'error'> = {
  STRONG: 'success',
  MODERATE: 'info',
  WEAK: 'warning',
  OFF_TARGET: 'error',
};

// ─── Main component ───────────────────────────────────────────────────────────

interface DraftReplyBlockProps {
  chat: Chat;
  onQueueSend: (
    message: string,
    chatId?: string,
    opts?: { fromDraft?: boolean; chatLabel?: string }
  ) => void;
  sendPipelineActive: boolean;
  isSending: boolean;
  draftSuppressedAfterSend: boolean;
}

export const DraftReplyBlock: React.FC<DraftReplyBlockProps> = ({
  chat,
  onQueueSend,
  sendPipelineActive,
  isSending,
  draftSuppressedAfterSend,
}) => {
  const [variantIndex, setVariantIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState('');
  const [isDismissed, setIsDismissed] = useState(false);
  const [campaignExpanded, setCampaignExpanded] = useState(false);
  const [customVariant, setCustomVariant] = useState<DraftVariant | null>(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedStrategy, setSelectedStrategy] = useState('');
  const [customDraftMode, setCustomDraftMode] = useState<CustomDraftMode>('matrix');
  const [showEnglishTranslation, setShowEnglishTranslation] = useState(false);

  const { data: strategyNames = [] } = useQuery({
    queryKey: ['draft-strategy-names'],
    queryFn: () => chatApi.getDraftStrategyNames(),
    staleTime: 60 * 60 * 1000,
  });
  const { data: signalUiCatalog = [] } = useQuery({
    queryKey: ['conversation-signals-ui-catalog'],
    queryFn: chatApi.getConversationSignalsCatalog,
    staleTime: 60 * 60 * 1000,
  });
  const signalUiByKey = useMemo(
    () => buildConversationSignalUiMap(signalUiCatalog),
    [signalUiCatalog]
  );

  useEffect(() => {
    if (!selectedStrategy && strategyNames.length > 0) {
      setSelectedStrategy(strategyNames[0]);
    }
  }, [strategyNames, selectedStrategy]);

  const generateMutation = useMutation({
    mutationFn: () =>
      customDraftMode === 'own'
        ? chatApi.generateCustomDraft(chat.key, { custom_prompt: customPrompt.trim() })
        : chatApi.generateCustomDraft(chat.key, { strategy_name: selectedStrategy || undefined }),
    onSuccess: (v) => {
      setCustomVariant(v);
      setIsEditing(false);
      setEditedText('');
    },
  });

  useEffect(() => {
    setIsDismissed(false);
    setVariantIndex(0);
    setIsEditing(false);
    setEditedText('');
    setCampaignExpanded(false);
    setCustomVariant(null);
    setCustomPrompt('');
    setSelectedStrategy('');
    setCustomDraftMode('matrix');
    setShowEnglishTranslation(false);
  }, [chat.key]);

  const preVariants: DraftVariant[] = useMemo(
    () => chat.draft_reply_variants ?? [],
    [chat.draft_reply_variants],
  );

  const matrixCount = preVariants.length;
  const customSlotIndex = matrixCount;
  const dotCount = matrixCount + 1;

  useEffect(() => {
    setVariantIndex((i) => Math.min(i, customSlotIndex));
  }, [customSlotIndex]);

  useEffect(() => {
    setShowEnglishTranslation(false);
  }, [variantIndex, customVariant?.message_text, preVariants]);

  const topSignals = useMemo(() => {
    const raw = chat.conversation_signals ?? [];
    return [...raw]
      .sort((a, b) => b.strength_value - a.strength_value)
      .slice(0, 3);
  }, [chat.conversation_signals]);

  const primaryForStrength = useMemo(
    () => topSignals.find((s) => s.is_primary) ?? topSignals[0],
    [topSignals],
  );

  if (
    draftSuppressedAfterSend ||
    isDismissed ||
    preVariants.length === 0 ||
    chat.direction !== 'inbound'
  ) {
    return null;
  }

  const isCustomSlot = variantIndex === customSlotIndex;
  const matrixVariant = !isCustomSlot ? preVariants[variantIndex] : null;
  const activeVariant: DraftVariant | null = isCustomSlot ? customVariant : matrixVariant;

  const stream = chat.stream;
  const sendLocked = sendPipelineActive || isSending || generateMutation.isPending;
  const customFormVisible = isCustomSlot && !customVariant;
  const activeLanguageCode = (activeVariant?.language_code || '').toLowerCase();
  const hasEnglishTranslation =
    !!activeVariant?.english_translation?.trim() &&
    activeLanguageCode !== '' &&
    activeLanguageCode !== 'en';

  const displayText =
    isEditing && activeVariant ? editedText : (activeVariant?.message_text ?? '');
  const canGenerateCustom =
    customDraftMode === 'own'
      ? !!customPrompt.trim() && !sendLocked
      : !!selectedStrategy?.trim() && !sendLocked;

  const prospectLabel =
    chat.prospect?.full_name ||
    (chat.prospect?.first_name && chat.prospect?.last_name
      ? `${chat.prospect.first_name} ${chat.prospect.last_name}`
      : null) ||
    chat.prospect?.first_name ||
    chat.prospect?.last_name ||
    'Unknown';

  const handleSend = () => {
    if (!activeVariant || sendLocked) return;
    const text = (isEditing ? editedText : activeVariant.message_text).trim();
    if (!text) return;
    onQueueSend(text, chat.key, { fromDraft: true, chatLabel: prospectLabel });
  };

  const handleEditClick = () => {
    if (!activeVariant) return;
    setEditedText(activeVariant.message_text);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedText('');
  };

  const handleBackToCustomStrategySelection = () => {
    if (sendLocked) return;
    setCustomVariant(null);
    setIsEditing(false);
    setEditedText('');
    setShowEnglishTranslation(false);
  };

  const handlePrev = () => {
    if (variantIndex > 0) {
      setVariantIndex((i) => i - 1);
      setIsEditing(false);
    }
  };

  const handleNext = () => {
    if (variantIndex < customSlotIndex) {
      setVariantIndex((i) => i + 1);
      setIsEditing(false);
    }
  };

  const strategyTitle = customFormVisible
    ? 'Custom strategy'
    : activeVariant?.strategy_name ?? '';
  const rationaleText = customFormVisible ? '' : activeVariant?.rationale ?? '';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        width: '100%',
        mt: 0.5,
        mb: 2,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          maxWidth: '70%',
          minWidth: 0,
          width: '100%',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          opacity: sendLocked ? 0.92 : 1,
          transition: 'opacity 0.15s',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexWrap: 'wrap',
            px: 1.5,
            py: 0.75,
            bgcolor: 'action.hover',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <AiIcon sx={{ fontSize: 15, color: 'primary.main' }} />
          <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.6, flexShrink: 0 }}>
            AI DRAFT
          </Typography>

          {topSignals.map((s, idx) => {
            const ui = getConversationSignalUi(s.signal, signalUiByKey) ?? getFallbackSignalUi(s.signal);
            const label = ui.label;
            const desc = ui.description;
            const colors = ui.filter;
            const tip = [desc, s.evidence ? `“${s.evidence}”` : ''].filter(Boolean).join(' — ');
            return (
              <Tooltip key={`${s.signal}-${idx}`} title={tip} arrow>
                <Chip
                  label={label}
                  size="small"
                  sx={{
                    bgcolor: colors.bg,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    fontSize: '0.68rem',
                    height: '20px',
                    fontWeight: 700,
                    cursor: 'default',
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Tooltip>
            );
          })}

          {primaryForStrength && (
            <Chip
              label={primaryForStrength.strength_text.replace('_', '-')}
              size="small"
              color={MATCH_STRENGTH_COLORS[primaryForStrength.strength_text]}
              sx={{ ml: 'auto', fontSize: '0.65rem', height: '18px', fontWeight: 600 }}
            />
          )}
        </Box>

        <Box sx={{ px: 1.5, pt: 1.25, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {strategyTitle}
              </Typography>
              {customFormVisible && (
                <Chip
                  label="Your call"
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{ fontSize: '0.62rem', height: '16px', '& .MuiChip-label': { px: 0.6 } }}
                />
              )}
              {activeVariant?.is_recommended && !isCustomSlot && (
                <Chip
                  label="Recommended"
                  size="small"
                  color="success"
                  sx={{ fontSize: '0.62rem', height: '16px', '& .MuiChip-label': { px: 0.6 } }}
                />
              )}
            </Box>
            {customFormVisible && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Choose one source: a matrix strategy from the list, or your own strategy in text — not both.
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {Array.from({ length: dotCount }, (_, i) => (
              <Box
                key={i}
                onClick={() => {
                  if (!sendLocked) {
                    setVariantIndex(i);
                    setIsEditing(false);
                  }
                }}
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: i === variantIndex ? 'primary.main' : 'grey.300',
                  cursor: sendLocked ? 'default' : 'pointer',
                  transition: 'background-color 0.15s',
                  ...(!sendLocked && {
                    '&:hover': { bgcolor: i === variantIndex ? 'primary.dark' : 'grey.400' },
                  }),
                }}
              />
            ))}
          </Box>
        </Box>

        {!customFormVisible && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontStyle: 'italic', px: 1.5, pb: 0.75, display: 'block', lineHeight: 1.5 }}
          >
            {rationaleText}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'stretch', px: 0.5, pb: 0.5 }}>
          <IconButton
            size="small"
            onClick={handlePrev}
            disabled={variantIndex === 0 || isEditing || sendLocked}
            sx={{ alignSelf: 'center', color: 'text.secondary', '&:disabled': { opacity: 0.25 } }}
          >
            <PrevIcon />
          </IconButton>

          <Box sx={{ flex: 1, mx: 0.25 }}>
            {customFormVisible ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, py: 0.5 }}>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={customDraftMode}
                  onChange={(_, v: CustomDraftMode | null) => {
                    if (v !== null && !sendLocked) setCustomDraftMode(v);
                  }}
                  disabled={sendLocked}
                  color="primary"
                  sx={{ '& .MuiToggleButton-root': { py: 0.5, fontSize: '0.75rem', textTransform: 'none' } }}
                >
                  <ToggleButton value="matrix">Matrix strategy</ToggleButton>
                  <ToggleButton value="own">Your strategy</ToggleButton>
                </ToggleButtonGroup>
                {customDraftMode === 'matrix' ? (
                  <FormControl size="small" fullWidth disabled={sendLocked}>
                    <InputLabel id={`draft-strat-${chat.key}`}>Strategy</InputLabel>
                    <Select
                      labelId={`draft-strat-${chat.key}`}
                      label="Strategy"
                      value={selectedStrategy}
                      onChange={(e) => setSelectedStrategy(e.target.value)}
                    >
                      {strategyNames.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    multiline
                    minRows={2}
                    maxRows={6}
                    size="small"
                    fullWidth
                    label="Your strategy"
                    placeholder="Describe how you want to angle the reply (this replaces a matrix pick)"
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    disabled={sendLocked}
                  />
                )}
                <Button
                  variant="contained"
                  color="info"
                  size="small"
                  startIcon={
                    generateMutation.isPending ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : (
                      <AiIcon sx={{ fontSize: 16 }} />
                    )
                  }
                  disabled={!canGenerateCustom}
                  onClick={() => generateMutation.mutate()}
                >
                  Generate draft
                </Button>
              </Box>
            ) : isEditing && activeVariant ? (
              <TextField
                multiline
                fullWidth
                minRows={3}
                maxRows={8}
                value={editedText}
                onChange={(e) => setEditedText(e.target.value)}
                size="small"
                variant="outlined"
                autoFocus
                disabled={sendLocked}
                sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem', bgcolor: 'grey.50' } }}
              />
            ) : (
              <Box
                sx={{
                  p: 1.25,
                  bgcolor: 'grey.50',
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  minHeight: 72,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.65,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {displayText}
                </Typography>
                {hasEnglishTranslation && showEnglishTranslation && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'grey.100',
                      color: 'text.primary',
                      fontStyle: 'italic',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {activeVariant?.english_translation}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <IconButton
            size="small"
            onClick={handleNext}
            disabled={variantIndex >= customSlotIndex || isEditing || sendLocked}
            sx={{ alignSelf: 'center', color: 'text.secondary', '&:disabled': { opacity: 0.25 } }}
          >
            <NextIcon />
          </IconButton>
        </Box>

        {stream && (
          <Box sx={{ px: 1.5, pb: 0.5 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.25,
                cursor: 'pointer',
                userSelect: 'none',
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
              }}
              onClick={() => {
                if (!sendLocked) setCampaignExpanded((v) => !v);
              }}
            >
              {campaignExpanded ? (
                <CollapseIcon sx={{ fontSize: 15 }} />
              ) : (
                <ExpandIcon sx={{ fontSize: 15 }} />
              )}
              <Typography variant="caption">Campaign context</Typography>
            </Box>
            {campaignExpanded && (
              <Box
                sx={{
                  mt: 0.5,
                  p: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.4,
                }}
              >
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 80, color: 'text.secondary' }}>
                    Experiment
                  </Typography>
                  <Typography variant="caption">{stream.name}</Typography>
                </Box>
                {stream.notes && (
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, minWidth: 80, color: 'text.secondary' }}>
                      Notes
                    </Typography>
                    <Typography variant="caption" sx={{ lineHeight: 1.5 }}>
                      {stream.notes}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          {isEditing ? (
            <Button
              size="small"
              variant="text"
              color="inherit"
              onClick={handleCancelEdit}
              sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 0 }}
            >
              Cancel
            </Button>
          ) : (
            <Button
              size="small"
              variant="text"
              startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              onClick={handleEditClick}
              disabled={sendLocked || !activeVariant || customFormVisible}
              sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 0 }}
            >
              Edit
            </Button>
          )}

          {!customFormVisible && activeVariant && hasEnglishTranslation && (
            <Button
              size="small"
              variant="text"
              onClick={() => setShowEnglishTranslation((v) => !v)}
              disabled={sendLocked}
              sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 0 }}
            >
              {showEnglishTranslation ? 'Hide English translation' : 'Show English translation'}
            </Button>
          )}

          {isCustomSlot && !!customVariant && (
            <Button
              size="small"
              variant="text"
              onClick={handleBackToCustomStrategySelection}
              disabled={sendLocked}
              sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 0 }}
            >
              Back to strategy selection
            </Button>
          )}

          <Button
            size="small"
            variant="text"
            startIcon={<DismissIcon sx={{ fontSize: 14 }} />}
            onClick={() => setIsDismissed(true)}
            disabled={sendLocked}
            sx={{ fontSize: '0.75rem', color: 'text.secondary', minWidth: 0 }}
          >
            Dismiss
          </Button>

          <Button
            size="small"
            variant="contained"
            startIcon={<SendIcon sx={{ fontSize: 14 }} />}
            onClick={handleSend}
            disabled={
              customFormVisible ||
              !activeVariant ||
              (isEditing && !editedText.trim()) ||
              sendLocked
            }
            sx={{ ml: 'auto', fontSize: '0.75rem' }}
          >
            Send
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
